import React, {
  FormEvent,
  FunctionComponent,
  useCallback,
  useEffect,
  useState,
} from 'react';
import styled from 'styled-components';

import THEME from 'theme';
import { FormGroup, Message } from 'components/chroma';
import { TextSmall } from 'components/chroma/extended';
import { CardList, SubmitButton } from 'components';

import {
  InsuredItem,
  RefDataType,
  SingleFieldStepProps,
} from 'scenes/common/types';
import {
  customerCareNumber,
  format1300Number,
} from 'scenes/common/helpers/contacts';

import { SecurityFeatureCard } from './SecurityFeatureCard';
import {
  NO_ALARM,
  selectedSecurityFeaturesCopy,
} from './services/securityFeature';

const ErrorWrapper = styled.div`
  margin-bottom: 2rem;
  .c-text {
    color: ${THEME.TEXT.ERROR};
  }
`;

export const ERROR_MESSAGE = `You said you have installed security at your home since it was broken into, but you chose "None of the above" when asked about your home's security features. Please indicate which security features your home has, or else call us on ${format1300Number(
  customerCareNumber()
)} to discuss.`;

const isValid = (items: string[], error: string): boolean =>
  items.length > 0 && !error;

const isSelected = (
  securityFeature: string,
  selectedSpecialDetails: string[]
): boolean =>
  selectedSpecialDetails.some((option) => option === securityFeature);

const SecurityFeaturesStep: FunctionComponent<
  Pick<SingleFieldStepProps<string[]>, 'defaultStateValue' | 'onNext'> & {
    homeSecurities: RefDataType[];
    hasAdditionalSecurityInstalled: boolean;
    insuredItem: InsuredItem;
  }
> = ({
  defaultStateValue,
  onNext,
  homeSecurities,
  hasAdditionalSecurityInstalled,
  insuredItem,
}) => {
  const question = `Which of the following security features, if any, does your ${insuredItem} have?`;

  const [selectedSecurityFeatures, setSelectedSecurityFeatures] = useState(
    defaultStateValue
  );
  const [error, setError] = useState('');

  const onSubmit = useCallback(
    (event: FormEvent) => {
      onNext({ securityFeatures: selectedSecurityFeatures });
      event.preventDefault();
    },
    [onNext, selectedSecurityFeatures]
  );

  const handleSelect = useCallback(
    (securityFeature: string) => {
      setSelectedSecurityFeatures(
        selectedSecurityFeaturesCopy(selectedSecurityFeatures, securityFeature)
      );
    },
    [selectedSecurityFeatures]
  );

  useEffect(() => {
    setError(
      hasAdditionalSecurityInstalled &&
        selectedSecurityFeatures.includes(NO_ALARM)
        ? ERROR_MESSAGE
        : ''
    );
  }, [hasAdditionalSecurityInstalled, selectedSecurityFeatures]);

  return (
    <form onSubmit={onSubmit}>
      <FormGroup legend={question}>
        <CardList marginBottom="1rem">
          {homeSecurities.map((securityFeature, index) => (
            <SecurityFeatureCard
              securityFeature={securityFeature}
              key={securityFeature.value}
              selected={isSelected(
                securityFeature.value,
                selectedSecurityFeatures
              )}
              onClick={() => handleSelect(securityFeature.value)}
              screenReaderText={`Option ${index + 1} of ${
                homeSecurities.length
              }`}
            />
          ))}
        </CardList>
      </FormGroup>
      {error && (
        <ErrorWrapper>
          <Message error>
            <TextSmall>{error}</TextSmall>
          </Message>
        </ErrorWrapper>
      )}
      <SubmitButton enabled={isValid(selectedSecurityFeatures, error)}>
        Next
      </SubmitButton>
    </form>
  );
};

export default SecurityFeaturesStep;
